import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Backdrop,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { getAllUsers, addUser, updateUser, deleteUser, emailExists, usernameExists } from './firebaseService';
import { createTestUser } from './setupTestUser';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import FilterListIcon from '@mui/icons-material/FilterList';

const AdminDashboard = ({ onLogout }) => {
    // ...existing code...
    const handleCreateTestUser = async () => {
      setIsSaving(true);
      try {
        // Add test user to localStorage for local login testing
        const testUser = {
          username: 'testuser',
          password: 'test123',
          email: 'testuser@example.com',
          name: 'Test User',
          active: true,
          createdAt: new Date().toISOString()
        };
        let appUsers = [];
        const storedUsers = localStorage.getItem('appUsers');
        if (storedUsers) {
          appUsers = JSON.parse(storedUsers);
          // Remove any existing testuser
          appUsers = appUsers.filter(u => u.username !== 'testuser');
        }
        appUsers.push(testUser);
        localStorage.setItem('appUsers', JSON.stringify(appUsers));
        showSnackbar('Test user added to localStorage! Username: testuser, Password: test123', 'success');
        await loadUsers();
      } catch (error) {
        showSnackbar('Failed to add test user locally', 'error');
      }
      setIsSaving(false);
    };
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    agency: '',
    active: true,
    billed: false,
    gpsAccess: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [billedFilter, setBilledFilter] = useState('all');
  const [activities, setActivities] = useState([]);
  const [activitiesDialogOpen, setActivitiesDialogOpen] = useState(false);
  const [activitiesSearchQuery, setActivitiesSearchQuery] = useState('');

  useEffect(() => {
    loadUsers();
    loadActivities();
  }, []);

  const loadActivities = () => {
    const storedActivities = localStorage.getItem('userActivities');
    if (storedActivities) {
      setActivities(JSON.parse(storedActivities));
    }
  };

  const addActivity = (username, action, details = '', isError = false) => {
    const newActivity = {
      id: Date.now(),
      username,
      action,
      details,
      isError,
      timestamp: new Date().toISOString()
    };
    const updatedActivities = [newActivity, ...activities].slice(0, 100); // Keep last 100 activities
    setActivities(updatedActivities);
    localStorage.setItem('userActivities', JSON.stringify(updatedActivities));
  };

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      
      // Try to get users from Firebase first
      const firebaseUsers = await getAllUsers();
      
      if (firebaseUsers.length === 0) {
        // No users in Firebase, check localStorage for migration
        const storedUsers = localStorage.getItem('appUsers');
        if (storedUsers) {
          const localUsers = JSON.parse(storedUsers);
          
          // Migrate each user to Firebase
          for (const user of localUsers) {
            try {
              await addUser({
                username: user.username,
                password: user.password,
                active: user.active !== undefined ? user.active : true
              });
            } catch (err) {
              console.error('Error migrating user:', user.username, err);
            }
          }
          
          // Reload users from Firebase after migration
          const migratedUsers = await getAllUsers();
          setUsers(migratedUsers);
          showSnackbar(`Successfully migrated ${localUsers.length} users to Firebase!`, 'success');
          
          // Clear localStorage after successful migration
          localStorage.removeItem('appUsers');
        } else {
          setUsers([]);
        }
      } else {
        setUsers(firebaseUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showSnackbar('Error connecting to database. Please check your Firebase configuration.', 'error');
    } finally {
      setIsLoading(false);
    }
  };



  const validateForm = async () => {
    const errors = {};
    if (!formData.username || formData.username.trim().length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    if (!formData.password || formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!formData.agency || formData.agency.trim().length < 2) {
      errors.agency = 'Agency must be at least 2 characters';
    }
    
    // Check for duplicate username in Firebase
    try {
      const usernameTaken = await usernameExists(formData.username, editingUser?.id);
      if (usernameTaken) {
        errors.username = 'Username already exists';
      }
    } catch (error) {
      console.error('Error checking username:', error);
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: user.password,
        agency: user.agency || '',
        active: user.active,
        billed: user.billed === true,
        gpsAccess: user.gpsAccess === true
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        agency: '',
        active: true,
        billed: false,
        gpsAccess: false
      });
    }
    setFormErrors({});
    setShowPassword(false);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ username: '', password: '', agency: '', active: true, billed: false, gpsAccess: false });
    setFormErrors({});
    setShowPassword(false);
  };

  const handleSaveUser = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      showSnackbar('Please fix the errors in the form', 'error');
      return;
    }

    try {
      setIsSaving(true);
      
      if (editingUser) {
        // Update existing user
        await updateUser(editingUser.id, formData);
        addActivity('Admin', 'Updated user', `Modified ${formData.username}`);
        showSnackbar('User updated successfully', 'success');
      } else {
        // Add new user
        await addUser(formData);
        addActivity('Admin', 'Created user', `Added new user: ${formData.username}`);
        showSnackbar('User created successfully', 'success');
      }

      // Reload users from Firebase
      await loadUsers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving user:', error);
      addActivity('Admin', 'Error saving user', `Failed to save ${formData.username}: ${error.message}`, true);
      showSnackbar('Error saving user. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setIsSaving(true);
        await deleteUser(userId);
        addActivity('Admin', 'Deleted user', `Removed user: ${user?.username || 'Unknown'}`);
        await loadUsers();
        showSnackbar('User deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting user:', error);
        addActivity('Admin', 'Error deleting user', `Failed to delete ${user?.username || 'Unknown'}: ${error.message}`, true);
        showSnackbar('Error deleting user. Please try again.', 'error');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      setIsSaving(true);
      const user = users.find(u => u.id === userId);
      await updateUser(userId, { ...user, active: !user.active });
      addActivity('Admin', user.active ? 'Deactivated user' : 'Activated user', `${user.username}`);
      await loadUsers();
      showSnackbar(`User ${user.active ? 'deactivated' : 'activated'} successfully`, 'success');
    } catch (error) {
      console.error('Error toggling user status:', error);
      addActivity('Admin', 'Error updating status', `Failed to toggle status for ${user?.username || 'Unknown'}: ${error.message}`, true);
      showSnackbar('Error updating user status. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };



  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter users based on search query and billed filter
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    const username = (user.username || '').toLowerCase();
    const agency = (user.agency || '').toLowerCase();
    const matchesSearch = username.includes(query) || agency.includes(query);
    const isBilled = user.billed === true;
    const matchesBilled =
      billedFilter === 'all' ||
      (billedFilter === 'yes' && isBilled) ||
      (billedFilter === 'no' && !isBilled);
    return matchesSearch && matchesBilled;
  });

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.active).length;
  const inactiveUsers = totalUsers - activeUsers;

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: { xs: 1.5, sm: 2, md: 3 }
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading users from Firebase...
          </Typography>
        </Box>
      </Backdrop>

      {/* Saving Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSaving}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Saving changes...
          </Typography>
        </Box>
      </Backdrop>

      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateTestUser}
                  sx={{ mb: 2 }}
                  disabled={isSaving}
                >
                  Create Test User
                </Button>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <AdminPanelSettingsIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'white' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}>
              Admin Dashboard
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={() => setActivitiesDialogOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.15)',
                px: 2,
                py: 1,
                borderRadius: 2,
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.25)'
                }
              }}
            >
              <HistoryIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Activities
              </Typography>
              {activities.length > 0 && (
                <Chip
                  label={activities.length}
                  size="small"
                  sx={{
                    bgcolor: 'white',
                    color: '#667eea',
                    fontWeight: 700,
                    height: 20,
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Button>
            <Button
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
                      {totalUsers}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Total Users</Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: { xs: 36, sm: 48, md: 60 }, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
                      {activeUsers}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Active Users</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: { xs: 36, sm: 48, md: 60 }, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)', color: 'white' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
                      {inactiveUsers}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Inactive Users</Typography>
                  </Box>
                  <CancelIcon sx={{ fontSize: { xs: 36, sm: 48, md: 60 }, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              User Management
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              <TextField
                placeholder="🔍 Search by username or agency..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0); // Reset to first page on search
                }}
                variant="outlined"
                size="small"
                sx={{
                  width: { xs: '100%', sm: '250px', md: '300px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'white',
                    borderRadius: 3,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 500,
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)',
                    transition: 'all 0.3s ease',
                    '& fieldset': {
                      borderColor: 'transparent',
                      borderWidth: 2
                    },
                    '&:hover': {
                      boxShadow: '0 6px 25px rgba(102, 126, 234, 0.25)',
                      transform: 'translateY(-2px)',
                      '& fieldset': {
                        borderColor: '#667eea',
                      }
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 8px 30px rgba(102, 126, 234, 0.35)',
                      transform: 'translateY(-3px)',
                      '& fieldset': {
                        borderColor: '#667eea',
                        borderWidth: 2
                      }
                    },
                    '& input': {
                      padding: { xs: '10px 12px', sm: '12px 14px' }
                    }
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1,
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                      }}>
                        <SearchIcon sx={{ color: 'white', fontSize: 18 }} />
                      </Box>
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          setPage(0);
                        }}
                        sx={{
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                          '&:hover': {
                            bgcolor: 'rgba(102, 126, 234, 0.2)',
                            transform: 'rotate(90deg)',
                            transition: 'all 0.3s ease'
                          }
                        }}
                      >
                        <CancelIcon sx={{ fontSize: 18, color: '#667eea' }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: '100%', sm: 170 },
                  background: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    fontWeight: 500,
                    '& fieldset': { borderColor: 'transparent', borderWidth: 2 },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: 2 }
                  }
                }}
              >
                <InputLabel id="billed-filter-label">Billed</InputLabel>
                <Select
                  labelId="billed-filter-label"
                  label="Billed"
                  value={billedFilter}
                  onChange={(e) => {
                    setBilledFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Billed: Yes</MenuItem>
                  <MenuItem value="no">Billed: No</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.25 },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  }
                }}
              >
                Add New User
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table sx={{ minWidth: { xs: 500, sm: 650 } }}>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}>
                  <TableCell sx={{ 
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      Username
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      Password
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      Agency
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon fontSize="small" />
                      Status
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoneyIcon fontSize="small" />
                      Billed
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }} align="center">
                    📍 GPS Tracer
                  </TableCell>
                  <TableCell sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }} align="center">
                    🕒 Last Login (ET)
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    py: 2.5,
                    borderBottom: 'none'
                  }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        {searchQuery ? `No users found matching "${searchQuery}"` : 'No users found. Click "Add New User" to create one.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="action" fontSize="small" />
                          <Typography sx={{ fontWeight: 500 }}>{user.username}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'monospace' }}>{'•'.repeat(user.password.length)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon color="action" fontSize="small" />
                          <Typography>{user.agency || 'N/A'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.active ? <CheckCircleIcon /> : <CancelIcon />}
                          label={user.active ? 'Active' : 'Inactive'}
                          color={user.active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={user.billed ? <AttachMoneyIcon /> : <MoneyOffIcon />}
                          label={user.billed ? 'Billed' : 'Not Billed'}
                          color={user.billed ? 'success' : 'warning'}
                          size="small"
                          variant={user.billed ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={user.gpsAccess ? 'GPS Tracer enabled — click to disable' : 'GPS Tracer disabled — click to enable'}>
                          <Chip
                            label={user.gpsAccess ? '✓ Enabled' : '✕ Disabled'}
                            size="small"
                            onClick={async () => {
                              try {
                                await updateUser(user.id, { ...user, gpsAccess: !user.gpsAccess });
                                addActivity('Admin', user.gpsAccess ? 'Disabled GPS Tracer' : 'Enabled GPS Tracer', user.username);
                                await loadUsers();
                                showSnackbar(`GPS Tracer ${user.gpsAccess ? 'disabled' : 'enabled'} for ${user.username}`, 'success');
                              } catch (e) {
                                showSnackbar('Failed to update GPS access', 'error');
                              }
                            }}
                            sx={{
                              fontWeight: 700,
                              cursor: 'pointer',
                              background: user.gpsAccess ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' : '#e5e7eb',
                              color: user.gpsAccess ? '#fff' : '#6b7280',
                              border: user.gpsAccess ? 'none' : '1px solid #cbd5e1',
                              '&:hover': {
                                background: user.gpsAccess ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : '#cbd5e1'
                              }
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {user.lastLogin ? (() => {
                          const d = new Date(user.lastLogin);
                          const dateStr = d.toLocaleDateString('en-US', {
                            timeZone: 'America/New_York',
                            month: 'short', day: '2-digit', year: 'numeric'
                          });
                          const timeStr = d.toLocaleTimeString('en-US', {
                            timeZone: 'America/New_York',
                            hour: '2-digit', minute: '2-digit', hour12: true
                          });
                          // Relative "ago" label
                          const diffMs = Date.now() - d.getTime();
                          const diffMin = Math.floor(diffMs / 60000);
                          let ago = '';
                          if (diffMin < 1) ago = 'just now';
                          else if (diffMin < 60) ago = `${diffMin} min ago`;
                          else if (diffMin < 60 * 24) ago = `${Math.floor(diffMin / 60)}h ago`;
                          else ago = `${Math.floor(diffMin / (60 * 24))}d ago`;
                          return (
                            <Tooltip title={`${dateStr} ${timeStr} ET (Harrisburg, PA) — ${ago}`}>
                              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                <Box sx={{
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  color: '#0f172a',
                                  fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace'
                                }}>
                                  {dateStr}
                                </Box>
                                <Box sx={{
                                  fontSize: '0.72rem',
                                  color: '#475569',
                                  fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace'
                                }}>
                                  {timeStr} <Box component="span" sx={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 600 }}>ET</Box>
                                </Box>
                                <Box sx={{
                                  fontSize: '0.62rem',
                                  color: '#64748b',
                                  background: '#f1f5f9',
                                  px: 0.75, py: 0.1,
                                  borderRadius: 0.5,
                                  fontWeight: 600
                                }}>
                                  {ago}
                                </Box>
                              </Box>
                            </Tooltip>
                          );
                        })() : (
                          <Box sx={{ color: '#cbd5e1', fontSize: '0.78rem', fontStyle: 'italic' }}>Never</Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={user.active ? 'Deactivate' : 'Activate'}>
                            <Switch
                              checked={user.active}
                              onChange={() => handleToggleActive(user.id)}
                              color="success"
                            />
                          </Tooltip>
                          <Tooltip title="Edit user">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(user)}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete user">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUser(user.id)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => {
                setFormData({ ...formData, username: e.target.value });
                setFormErrors({ ...formErrors, username: '' });
              }}
              error={!!formErrors.username}
              helperText={formErrors.username}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                setFormErrors({ ...formErrors, password: '' });
              }}
              error={!!formErrors.password}
              helperText={formErrors.password}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Agency"
              value={formData.agency}
              onChange={(e) => {
                setFormData({ ...formData, agency: e.target.value });
                setFormErrors({ ...formErrors, agency: '' });
              }}
              error={!!formErrors.agency}
              helperText={formErrors.agency}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  {formData.active ? 'Active' : 'Inactive'}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.billed}
                  onChange={(e) => setFormData({ ...formData, billed: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  {formData.billed ? 'Billed' : 'Not Billed'}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.gpsAccess}
                  onChange={(e) => setFormData({ ...formData, gpsAccess: e.target.checked })}
                  color="info"
                />
              }
              label={
                <Typography sx={{ fontWeight: 500 }}>
                  📍 GPS Tracer Access {formData.gpsAccess ? '— Enabled' : '— Disabled'}
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Activities Dialog */}
      <Dialog
        open={activitiesDialogOpen}
        onClose={() => {
          setActivitiesDialogOpen(false);
          setActivitiesSearchQuery('');
        }}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: { xs: '1rem', sm: '1.25rem' },
          p: { xs: 1.5, sm: 2 }
        }}>
          <HistoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          User Activities Log
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            p: 3, 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            borderBottom: '2px solid #e0e0e0',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              transform: 'scaleX(0)',
              transformOrigin: 'left',
              transition: 'transform 0.3s ease'
            },
            '&:focus-within::before': {
              transform: 'scaleX(1)'
            }
          }}>
            <TextField
              fullWidth
              placeholder="🔍 Search by username, action, or details..."
              value={activitiesSearchQuery}
              onChange={(e) => setActivitiesSearchQuery(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'white',
                  borderRadius: 3,
                  fontSize: '1rem',
                  fontWeight: 500,
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.15)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    borderColor: 'transparent',
                    borderWidth: 2
                  },
                  '&:hover': {
                    boxShadow: '0 6px 25px rgba(102, 126, 234, 0.25)',
                    transform: 'translateY(-2px)',
                    '& fieldset': {
                      borderColor: '#667eea',
                    }
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.35)',
                    transform: 'translateY(-3px)',
                    '& fieldset': {
                      borderColor: '#667eea',
                      borderWidth: 2
                    }
                  },
                  '& input': {
                    padding: '16px 14px'
                  }
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                    }}>
                      <SearchIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: activitiesSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setActivitiesSearchQuery('')}
                      sx={{
                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(102, 126, 234, 0.2)',
                          transform: 'rotate(90deg)',
                          transition: 'all 0.3s ease'
                        }
                      }}
                    >
                      <CancelIcon sx={{ fontSize: 18, color: '#667eea' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 500, overflow: 'auto', p: 2 }}>
            {activities.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <HistoryIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  No activities recorded yet.
                </Typography>
              </Box>
            ) : (
              activities
                .filter(activity => {
                  const query = activitiesSearchQuery.toLowerCase();
                  const username = (activity.username || '').toLowerCase();
                  const action = (activity.action || '').toLowerCase();
                  const details = (activity.details || '').toLowerCase();
                  return username.includes(query) || action.includes(query) || details.includes(query);
                })
                .map((activity) => (
                  <Box
                    key={activity.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      py: 2,
                      px: 2,
                      borderRadius: 2,
                      mb: 1.5,
                      bgcolor: activity.isError ? '#ffebee' : '#f9f9f9',
                      border: activity.isError ? '1px solid #ef5350' : '1px solid transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: activity.isError ? '#ffcdd2' : '#f0f0f0',
                        transform: 'translateX(4px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: activity.isError ? '#ef5350' : '#667eea',
                        mt: 1,
                        flexShrink: 0,
                        boxShadow: activity.isError ? '0 0 8px rgba(239, 83, 80, 0.4)' : '0 0 8px rgba(102, 126, 234, 0.4)'
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<PersonIcon sx={{ fontSize: 14 }} />}
                          label={activity.username}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: activity.isError ? '#ef5350' : '#667eea',
                            color: 'white'
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: activity.isError ? '#c62828' : '#333'
                          }}
                        >
                          {activity.action}
                        </Typography>
                        {activity.isError && (
                          <Chip
                            label="ERROR"
                            size="small"
                            sx={{
                              bgcolor: '#c62828',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                      {activity.details && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: activity.isError ? '#d32f2f' : '#666',
                            display: 'block',
                            mb: 0.5,
                            fontWeight: activity.isError ? 500 : 400
                          }}
                        >
                          {activity.details}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Chip
                          label={new Date(activity.timestamp).toLocaleDateString()}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Chip
                          label={new Date(activity.timestamp).toLocaleTimeString()}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))
            )}
            {activities.filter(activity => {
              const query = activitiesSearchQuery.toLowerCase();
              const username = (activity.username || '').toLowerCase();
              const action = (activity.action || '').toLowerCase();
              const details = (activity.details || '').toLowerCase();
              return username.includes(query) || action.includes(query) || details.includes(query);
            }).length === 0 && activitiesSearchQuery && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  No activities found matching "{activitiesSearchQuery}"
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ flex: 1, color: '#666', ml: 1 }}>
            Showing {activities.filter(activity => {
              const query = activitiesSearchQuery.toLowerCase();
              const username = (activity.username || '').toLowerCase();
              const action = (activity.action || '').toLowerCase();
              const details = (activity.details || '').toLowerCase();
              return username.includes(query) || action.includes(query) || details.includes(query);
            }).length} of {activities.length} activities
          </Typography>
          <Button
            onClick={() => {
              setActivitiesDialogOpen(false);
              setActivitiesSearchQuery('');
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
